import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { supabase, describeError, type Row } from '@/lib/supabase'
import type { ColorName } from '@/lib/colors'
import type { IsoDate } from '@/lib/dates'

/**
 * The pipeline board.
 *
 * The brief asks that this screen load in one query with no waterfalls, because
 * it is the one she opens every morning. PostgREST resource embedding does that:
 * the select below returns each job with both contacts and its venue already
 * attached, in a single round trip.
 *
 * The `!constraint_name` hints are not decoration — `jobs` has two foreign keys
 * to `contacts` and two to `venues`, so without them PostgREST cannot tell which
 * relationship is meant and refuses the request.
 */

const BOARD_SELECT = `
  id,
  job_number,
  event_date,
  pipeline_stage_id,
  stage_changed_at,
  services,
  guest_count,
  venue_name,
  distance_km,
  agreed_total,
  deposit_status,
  gallery_delivered_at,
  kind,
  title,
  team_confirmed_at,
  booked_at,
  inquiry:inquiries!jobs_inquiry_id_fkey ( status ),
  primary_contact:contacts!jobs_primary_contact_id_fkey ( id, first_name, last_name ),
  partner_contact:contacts!jobs_partner_contact_id_fkey ( id, first_name, last_name ),
  venue:venues!jobs_venue_id_fkey ( id, name, town, distance_km ),
  team:job_team ( role, status, member:team_members ( name ) ),
  labels:job_labels ( label:labels ( id, name, color ) ),
  deliverables:job_deliverables ( title, kind, internal, done_at, sort_order, steps:deliverable_steps ( title, done_at, sort_order ) )
` as const

type ContactStub = Pick<Row<'contacts'>, 'id' | 'first_name' | 'last_name'>

export interface BoardJob {
  id: string
  job_number: number
  event_date: IsoDate
  pipeline_stage_id: string
  stage_changed_at: string
  services: Row<'jobs'>['services']
  guest_count: number | null
  venue_name: string | null
  distance_km: number | null
  agreed_total: number | null
  deposit_status: Row<'jobs'>['deposit_status']
  gallery_delivered_at: string | null
  kind: Row<'jobs'>['kind']
  title: string | null
  team_confirmed_at: string | null
  booked_at: string | null
  inquiry: { status: Row<'inquiries'>['status'] } | null
  primary_contact: ContactStub | null
  partner_contact: ContactStub | null
  venue: { id: string; name: string; town: string | null; distance_km: number | null } | null
  team: Array<{ role: Row<'job_team'>['role']; status: Row<'job_team'>['status']; member: { name: string } | null }>
  labels: Array<{ label: { id: string; name: string | null; color: ColorName } | null }>
  deliverables: Array<{
    title: string
    kind: Row<'job_deliverables'>['kind']
    internal: boolean
    done_at: string | null
    sort_order: number
    steps: Array<{ title: string; done_at: string | null; sort_order: number }>
  }>
}

export const pipelineKeys = {
  stages: ['pipeline-stages'] as const,
  board: ['pipeline-board'] as const,
  job: (id: string) => ['job', id] as const,
  jobNotes: (id: string) => ['job-notes', id] as const,
}

export function useStages() {
  return useQuery({
    queryKey: pipelineKeys.stages,
    // Stages change when she reorganises her process, which is roughly never.
    staleTime: 10 * 60_000,
    queryFn: async (): Promise<Row<'pipeline_stages'>[]> => {
      const { data, error } = await supabase
        .from('pipeline_stages')
        .select('*')
        .is('archived_at', null)
        .order('sort_order')

      if (error) throw error
      return data
    },
  })
}

export function useBoard() {
  return useQuery({
    queryKey: pipelineKeys.board,
    queryFn: async (): Promise<BoardJob[]> => {
      // Dates move weddings along; the nightly job does this too, but opening
      // the board should never show yesterday's columns.
      await supabase.rpc('refresh_job_stages')
      const { data, error } = await supabase
        .from('jobs')
        .select(BOARD_SELECT)
        .is('archived_at', null)
        .order('event_date', { ascending: true })

      if (error) throw error
      return data as unknown as BoardJob[]
    },
  })
}

// ---------------------------------------------------------------------------
// A single wedding, everything on one screen
// ---------------------------------------------------------------------------

const JOB_SELECT = `
  *,
  primary_contact:contacts!jobs_primary_contact_id_fkey ( * ),
  partner_contact:contacts!jobs_partner_contact_id_fkey ( * ),
  venue:venues!jobs_venue_id_fkey ( * ),
  second_venue:venues!jobs_second_venue_id_fkey ( * ),
  stage:pipeline_stages!jobs_pipeline_stage_id_fkey ( * ),
  inquiry:inquiries!jobs_inquiry_id_fkey ( id, received_at, message, heard_about, status )
` as const

export interface JobDetail extends Row<'jobs'> {
  primary_contact: Row<'contacts'> | null
  partner_contact: Row<'contacts'> | null
  venue: Row<'venues'> | null
  second_venue: Row<'venues'> | null
  stage: Row<'pipeline_stages'> | null
  inquiry: Pick<Row<'inquiries'>, 'id' | 'received_at' | 'message' | 'heard_about' | 'status'> | null
}

export function useJob(id: string | undefined) {
  return useQuery({
    queryKey: pipelineKeys.job(id ?? ''),
    enabled: Boolean(id),
    queryFn: async (): Promise<JobDetail> => {
      const { data, error } = await supabase.from('jobs').select(JOB_SELECT).eq('id', id ?? '').single()
      if (error) throw error
      return data as unknown as JobDetail
    },
  })
}

export function useUpdateJob(id: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (patch: Partial<Row<'jobs'>>) => {
      const { data, error } = await supabase.from('jobs').update(patch).eq('id', id).select(JOB_SELECT).single()
      if (error) throw error
      return data as unknown as JobDetail
    },
    onSuccess: (data) => {
      queryClient.setQueryData(pipelineKeys.job(id), data)
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.board })
    },
    onError: (error) => {
      toast.error('Could not save', { description: describeError(error) })
    },
  })
}

// ---------------------------------------------------------------------------
// Internal notes
// ---------------------------------------------------------------------------

export interface JobNote extends Row<'job_notes'> {
  author: Pick<Row<'staff'>, 'id' | 'full_name'> | null
}

export function useJobNotes(jobId: string | undefined) {
  return useQuery({
    queryKey: pipelineKeys.jobNotes(jobId ?? ''),
    enabled: Boolean(jobId),
    queryFn: async (): Promise<JobNote[]> => {
      const { data, error } = await supabase
        .from('job_notes')
        .select('*, author:staff!job_notes_author_id_fkey ( id, full_name )')
        .eq('job_id', jobId ?? '')
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as unknown as JobNote[]
    },
  })
}

export function useAddJobNote(jobId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ body, authorId }: { body: string; authorId: string }) => {
      const { error } = await supabase.from('job_notes').insert({ job_id: jobId, body, author_id: authorId })
      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.jobNotes(jobId) })
    },
    onError: (error) => {
      toast.error('Could not add the note', { description: describeError(error) })
    },
  })
}

export function useDeleteJobNote(jobId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (noteId: string) => {
      const { error } = await supabase.from('job_notes').delete().eq('id', noteId)
      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.jobNotes(jobId) })
    },
    onError: (error) => {
      toast.error('Could not delete the note', { description: describeError(error) })
    },
  })
}

/** The couple's names, email and phone, edited from a project (28 Oct 2026). */
export function useUpdateContact(jobId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Pick<Row<'contacts'>, 'first_name' | 'last_name' | 'email' | 'phone'> }) => {
      const { error } = await supabase.from('contacts').update(patch).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.job(jobId) })
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.board })
    },
    onError: (error) => {
      toast.error('Could not save the contact', { description: describeError(error) })
    },
  })
}
